/**
 * Utility functions for converting TipTap node attrs.style objects to inline CSS strings.
 * Used by TipTap extensions to render block-level styles from JSON content.
 */

/**
 * Converts a camelCase CSS property name to kebab-case.
 * Example: "backgroundColor" -> "background-color"
 */
function camelToKebab(str: string): string {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/**
 * Style properties that represent CSS values (not attributes or data).
 * This whitelist ensures we only process valid CSS properties.
 */
const VALID_CSS_PROPERTIES = new Set([
  // Background
  'background',
  'backgroundColor',
  'backgroundImage',
  'backgroundSize',
  'backgroundPosition',
  'backgroundRepeat',
  // Colors
  'color',
  'opacity',
  // Box model
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  // Border
  'border',
  'borderWidth',
  'borderStyle',
  'borderColor',
  'borderTop',
  'borderRight',
  'borderBottom',
  'borderLeft',
  'borderRadius',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
  // Dimensions
  'width',
  'minWidth',
  'maxWidth',
  'height',
  'minHeight',
  'maxHeight',
  // Typography
  'fontSize',
  'fontWeight',
  'fontFamily',
  'fontStyle',
  'lineHeight',
  'letterSpacing',
  'textAlign',
  'textDecoration',
  'textTransform',
  // Layout
  'display',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'zIndex',
  'overflow',
  'overflowX',
  'overflowY',
  // Flexbox
  'flex',
  'flexDirection',
  'flexWrap',
  'justifyContent',
  'alignItems',
  'alignContent',
  'gap',
  'rowGap',
  'columnGap',
  // Grid
  'gridTemplateColumns',
  'gridTemplateRows',
  'gridColumn',
  'gridRow',
  // Effects
  'boxShadow',
  'textShadow',
  'transform',
  'transition',
  'filter',
  // Misc
  'cursor',
  'userSelect',
  'listStyle',
  'listStyleType',
  'whiteSpace',
  'wordBreak',
  'wordWrap',
]);

/**
 * Type for the style object that can be stored in TipTap node attrs.
 */
export interface StyleAttrs {
  style?: Record<string, string | number>;
  [key: string]: unknown;
}

/**
 * Converts a style object from TipTap node attrs to an inline CSS string.
 *
 * @param attrs - The node attributes object, which may contain a `style` property
 * @returns An inline CSS string (e.g., "background-color: #f0f9ff; padding: 16px;")
 *
 * @example
 * ```ts
 * const attrs = {
 *   style: {
 *     backgroundColor: '#f0f9ff',
 *     borderColor: '#0284c7',
 *     padding: '16px',
 *     borderRadius: '8px'
 *   }
 * };
 *
 * attrsToInlineStyle(attrs);
 * // Returns: "background-color: #f0f9ff; border-color: #0284c7; padding: 16px; border-radius: 8px;"
 * ```
 */
export function attrsToInlineStyle(attrs: StyleAttrs): string {
  if (!attrs?.style || typeof attrs.style !== 'object') {
    return '';
  }

  const styleObj = attrs.style;
  const cssRules: string[] = [];

  for (const [property, value] of Object.entries(styleObj)) {
    // Skip invalid properties or empty values
    if (!VALID_CSS_PROPERTIES.has(property)) {
      continue;
    }

    if (value === null || value === undefined || value === '') {
      continue;
    }

    const kebabProperty = camelToKebab(property);
    const stringValue = typeof value === 'number' ? `${value}px` : String(value);

    cssRules.push(`${kebabProperty}: ${stringValue}`);
  }

  return cssRules.length > 0 ? `${cssRules.join('; ')};` : '';
}

/**
 * Merges inline styles from attrs with additional static styles.
 *
 * @param attrs - The node attributes object with optional style property
 * @param additionalStyles - Additional CSS string to append
 * @returns Combined inline CSS string
 *
 * @example
 * ```ts
 * mergeInlineStyles({ style: { padding: '16px' } }, 'display: block;');
 * // Returns: "padding: 16px; display: block;"
 * ```
 */
export function mergeInlineStyles(attrs: StyleAttrs, additionalStyles?: string): string {
  const attrStyles = attrsToInlineStyle(attrs);
  const additional = additionalStyles?.trim() || '';

  if (!attrStyles && !additional) {
    return '';
  }

  if (!attrStyles) {
    return additional;
  }

  if (!additional) {
    return attrStyles;
  }

  // Ensure both end properly
  const normalizedAttr = attrStyles.endsWith(';') ? attrStyles : `${attrStyles};`;
  const normalizedAdditional = additional.endsWith(';') ? additional : `${additional};`;

  return `${normalizedAttr} ${normalizedAdditional}`.trim();
}

/**
 * Creates HTML attributes object with style property for use with TipTap's mergeAttributes.
 *
 * @param attrs - The node attributes object with optional style property
 * @param additionalStyles - Additional CSS string to append
 * @returns Object with style property if styles exist, empty object otherwise
 *
 * @example
 * ```ts
 * const styleAttrs = getStyleHTMLAttributes({ style: { padding: '16px' } });
 * // Returns: { style: "padding: 16px;" }
 *
 * // Use with mergeAttributes:
 * mergeAttributes(HTMLAttributes, styleAttrs, { class: 'my-class' });
 * ```
 */
export function getStyleHTMLAttributes(
  attrs: StyleAttrs,
  additionalStyles?: string
): { style?: string } {
  const style = mergeInlineStyles(attrs, additionalStyles);
  return style ? { style } : {};
}
